import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import PasswordIcon from '@mui/icons-material/Password';
import RefreshIcon from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { UseBreakpoints } from '@my2n/use-breakpoints';
import { useEffect } from 'react';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useAutoSizeText } from '../../hooks/useAutoSizeText';
import { useClipboard } from '../../hooks/useClipboard';
import { formatPinWithSpacing } from '../PinPanel/helpers/formatPinWithSpacing';
import { styles } from '../PinPanel/styles';
import messages from './messages';
import { customPinPanelStyles } from './styles';
import { getCustomPinErrorTestId, TestID } from './testIds';

export interface ICustomPinPanelProps {
  disableCopyButton: boolean;
  isFetching: boolean;
  hasError: boolean;
  isCustomPinMode: boolean;
  pin?: string;
  pinLengthSetting?: number;
  onInit: () => void;
  onGenerate: (() => void) | undefined;
  setIsCustomPinMode: (value: boolean) => void;
  setShouldValidateLength: (value: boolean) => void;
  showCustomPinButton: boolean;
}

export function CustomPinPanel({
  disableCopyButton,
  hasError,
  isCustomPinMode,
  isFetching,
  onGenerate,
  onInit,
  pin,
  pinLengthSetting,
  setIsCustomPinMode,
  setShouldValidateLength,
  showCustomPinButton,
}: ICustomPinPanelProps) {
  const { copyToClipboard } = useClipboard({
    copySuccessMessage: messages.pinPanelCopied,
  });
  const { upSm } = UseBreakpoints();
  const formMethods = useFormContext<{ customPin: string }>();
  const customPinValue = formMethods.watch('customPin') ?? '';
  const hasValidationError = isCustomPinMode && !formMethods.formState.isValid;
  const isCopyDisabled = !pin || isFetching || disableCopyButton;
  const formattedPin = formatPinWithSpacing(pin) || '####';
  const { ref: textFieldRef } = useAutoSizeText<HTMLDivElement>({
    resizeTrigger: customPinValue,
    targetSelector: 'input',
  });
  const { autoSizeTextStyle, ref: pinTextRef } = useAutoSizeText<HTMLHeadingElement>({
    resizeTrigger: formattedPin,
  });

  useEffect(() => {
    onInit();
  }, []);

  const onCopyToClipboard = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    const pinToCopy = isCustomPinMode ? formMethods.getValues('customPin') : pin;
    if (pinToCopy) {
      copyToClipboard(pinToCopy);
    }
  };

  const handleGenerateClick = () => {
    onGenerate!();
    setIsCustomPinMode(false);
  };

  const onCustomPinClick = () => {
    setIsCustomPinMode(true);
  };

  return (
    <>
      <Box
        sx={{
          ...styles.root,
          ...((hasError || hasValidationError) && styles.rootError),
        }}
      >
        <Box data-pin={pin} sx={showCustomPinButton ? customPinPanelStyles.pin : styles.pin}>
          {isCustomPinMode ? (
            <Controller
              name="customPin"
              control={formMethods.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  ref={textFieldRef}
                  value={field.value || ''}
                  helperText={undefined}
                  variant="outlined"
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, '');
                    field.onChange(numericValue);
                    setShouldValidateLength(false);
                  }}
                  onBlur={() => {
                    field.onBlur();
                    setShouldValidateLength(true);
                  }}
                  slotProps={{
                    input: {
                      inputProps: {
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                      },
                      sx: {
                        ...customPinPanelStyles.customPin,
                      },
                    },
                  }}
                />
              )}
            />
          ) : (
            <Typography ref={pinTextRef} sx={autoSizeTextStyle} variant="h2">
              {formattedPin}
            </Typography>
          )}
        </Box>
        <Box sx={customPinPanelStyles.customButtons}>
          <Stack
            direction={upSm ? 'column' : 'row'}
            spacing={2}
            sx={{ alignItems: 'center', flexWrap: upSm ? 'nowrap' : 'wrap', justifyContent: 'center' }}
          >
            <Button
              data-testid={TestID.GenerateButton}
              color="primary"
              size="small"
              disabled={isFetching}
              onClick={handleGenerateClick}
              startIcon={<RefreshIcon />}
            >
              <FormattedMessage {...messages.pinPanelGenerate} />
            </Button>
            {showCustomPinButton && (
              <Button
                data-testid={TestID.CustomPinButton}
                color={'primary'}
                size="small"
                onClick={onCustomPinClick}
                startIcon={<PasswordIcon />}
              >
                <FormattedMessage {...messages.userAccessFormPinCustom} />
              </Button>
            )}
            <Button
              data-testid={TestID.CopyButton}
              color="primary"
              size="small"
              disabled={isCopyDisabled}
              onClick={onCopyToClipboard}
              startIcon={<FileCopyOutlinedIcon />}
            >
              <FormattedMessage {...messages.pinPanelCopy} />
            </Button>
          </Stack>
        </Box>

        {isFetching && (
          <Box sx={styles.progressWrapper}>
            <LinearProgress sx={styles.progress} />
          </Box>
        )}
      </Box>
      {hasError && (
        <Typography data-testid={TestID.ErrorMessage} sx={styles.errorMessage} variant="body2">
          <FormattedMessage {...messages.pinPanelErrorMessage} />
        </Typography>
      )}
      {isCustomPinMode &&
        (formMethods.formState.errors.customPin ? (
          <Typography
            data-testid={getCustomPinErrorTestId(formMethods.formState.errors.customPin.type)}
            sx={styles.errorMessage}
            variant="body2"
          >
            {formMethods.formState.errors.customPin.message}
          </Typography>
        ) : (
          <Typography
            data-testid={TestID.CustomPinHelperMessage}
            sx={customPinPanelStyles.helperMessage}
            variant="body2"
          >
            <FormattedMessage {...messages.userPinModalHelperCustomPin} values={{ pinLength: pinLengthSetting }} />
          </Typography>
        ))}
    </>
  );
}
